// These are av-subsystem utility functions and helpers
// They import the avsubsystem so they should only be used in components that are lazy loaded with Av
// so they are not imported in the main bundle

import { AvLogSink, avSelectors, useAvContext } from "@minervaproject/avsubsystem";
import { useEffect, useMemo, useState } from "react";

export function useAvDeviceOptions() {
  const deviceList = avSelectors.useDeviceList();
  const protectedDeviceList = useMemo(() => deviceList || [], [deviceList]);

  const cameraOptions = useMemo(
    () =>
      protectedDeviceList
        .filter((device) => device.kind === "videoinput")
        .map((device) => ({
          title: device.label,
          id: device.id,
        })),
    [protectedDeviceList],
  );

  const micOptions = useMemo(
    () =>
      protectedDeviceList
        .filter((device) => device.kind === "audioinput")
        .map((device) => ({
          title: device.label,
          id: device.id,
        })),
    [protectedDeviceList],
  );

  const audioOutputOptions = useMemo(
    () =>
      protectedDeviceList
        .filter((device) => device.kind === "audiooutput")
        .map((device) => ({
          title: device.label,
          id: device.id,
        })),
    [protectedDeviceList],
  );

  return { cameraOptions, micOptions, audioOutputOptions };
}

export function useAudioLevelStream() {
  const avConnection = useAvContext();
  const [audioLevelStream, setAudioLevelStream] = useState<MediaStream | null>(null);
  const selectedDevices = avSelectors.useSelectedDevices();

  useEffect(() => {
    const localMediaStream = avConnection?.getLocalMediaStream();
    if (
      avConnection &&
      selectedDevices.selectedMic?.id &&
      localMediaStream &&
      localMediaStream.getAudioTracks().length > 0
    ) {
      const newAudioTrackId = localMediaStream.getAudioTracks()[0].id;
      const oldAudioTrackId = audioLevelStream?.getAudioTracks()[0]?.id;
      const shouldUpdateMediaStream =
        audioLevelStream === null || oldAudioTrackId !== newAudioTrackId;

      if (shouldUpdateMediaStream) {
        const newMediaStream = new MediaStream();
        newMediaStream.addTrack(localMediaStream.getAudioTracks()[0]);
        setAudioLevelStream(newMediaStream);
      }
    }
    return () => {
      if (audioLevelStream) {
        // The audio track is shared with the published stream.
        // It will be stopped by the AVsubsystem on close.
        audioLevelStream.getTracks().forEach((track) => audioLevelStream.removeTrack(track));
      }
    };
    // If there is a streamState we want to make sure we check the audioLevelStream when it changes
  }, [audioLevelStream, avConnection, selectedDevices]);

  return audioLevelStream;
}

export const avLoggerSink: AvLogSink = {
  log: (...args: unknown[]) => {
    console.info(...args);
  },
  // For now we are not using rollbar for av subsystem logs
  warn: (...args: unknown[]) => {
    console.warn(...args);
  },
  error: (...args: unknown[]) => {
    console.error(...args);
  },
};
